import React, { useRef, useState, useEffect } from "react";
import { Card, CardBody, Col, Container, Row, Button, Alert, Badge } from "reactstrap";
import { withTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import Breadcrumbs from "components/Common/Breadcrumb";
// import your API call:
import { getAllBookingByDateRange } from "api";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import { calcRevenueDetails } from "utils/revenueCalculator";

// FlatPickr imports
import "flatpickr/dist/themes/material_blue.css";
import FlatPickr from "react-flatpickr";

// Revenue calculation utility imported from utils/revenueCalculator

const paginationPageSize = 500;
const paginationPageSizeSelector = [200, 500, 1000];

const EventoxRevenueInDateRange = (props) => {
  document.title = "Revenue Report | EventoX";
  const navigate = useNavigate();
  const location = useLocation();
  const gridApiRef = useRef(null);

  // Get search state from sessionStorage or use defaults
  // This function retrieves previously saved search parameters and results
  // to restore the user's search state when returning from booking details
  const getSearchState = () => {
    const savedState = sessionStorage.getItem('revenueReportSearchState');
    if (savedState) {
      try {
        return JSON.parse(savedState);
      } catch (e) {
        console.error('Error parsing saved search state:', e);
      }
    }
    return {
      fromDate: "",
      toDate: "",
      bookings: [],
      summaryStats: {
        totalRevenue: 0,
        totalEventoxRevenue: 0,
        totalOrganizerRevenue: 0,
        totalStripeFee: 0,
        totalBookings: 0,
        totalTickets: 0,
        averageTicketPrice: 0
      }
    };
  };

  const [fromDate, setFromDate] = useState(getSearchState().fromDate);
  const [toDate, setToDate] = useState(getSearchState().toDate);
  const [bookings, setBookings] = useState(getSearchState().bookings);
  const [summaryStats, setSummaryStats] = useState(getSearchState().summaryStats);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [stateRestored, setStateRestored] = useState(false);

  // Save search state to sessionStorage
  // This function preserves the current search parameters and results
  // so they can be restored when returning from booking details
  const saveSearchState = () => {
    const searchState = {
      fromDate,
      toDate,
      bookings,
      summaryStats
    };
    sessionStorage.setItem('revenueReportSearchState', JSON.stringify(searchState));
  };

  // Clear saved search state
  const clearSearchState = () => {
    sessionStorage.removeItem('revenueReportSearchState');
  };

  // Navigate to booking details with state preservation
  // This function saves the current search state before navigating
  // to ensure it can be restored when the user returns
  const navigateToBookingDetails = (bookingNo) => {
    saveSearchState();
    navigate(`/admin/report/booking-details/${bookingNo}`);
  };

  // Effect to restore state when component mounts
  useEffect(() => {
    // Check if we're returning from a details page
    const savedState = sessionStorage.getItem('revenueReportSearchState');
    if (savedState && (bookings.length === 0 || fromDate === "")) {
      try {
        const parsedState = JSON.parse(savedState);
        if (parsedState.fromDate && parsedState.toDate) {
          setFromDate(parsedState.fromDate);
          setToDate(parsedState.toDate);
          if (parsedState.bookings && parsedState.bookings.length > 0) {
            setBookings(parsedState.bookings);
            setSummaryStats(parsedState.summaryStats);
            setStateRestored(true);
            // Clear the notification after 3 seconds
            setTimeout(() => setStateRestored(false), 3000);
          }
        }
      } catch (e) {
        console.error('Error restoring search state:', e);
      }
    }
  }, []);

  // Table column definitions
  const columns = [
    {
      headerName: "Booking No",
      field: "bookingNo",
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      headerName: "Event Name",
      field: "Event.eventName",
      valueGetter: params => params.data.Event?.eventName || "N/A",
      flex: 1,
      filter: true,
    },
    {
      headerName: "Ticket Sold",
      field: "ticketSold",
      valueGetter: params => params?.data?.ticketSold || params?.data?.totalTicketCount || 0,
      flex: 1,
      filter: true,
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Total Ticket Amount",
      field: "totalTicketAmount",
      valueGetter: params => params.data.totalTicketAmount || 0,
      flex: 1,
      filter: true,
      valueFormatter: params => `₹${params?.value?.toFixed(2) || 0}`,
    },
    {
      headerName: "Discount",
      field: "discount",
      valueGetter: params => params?.data?.discountAmount || 0,
      flex: 1,
      filter: true,
      valueFormatter: params => `₹${params?.value?.toFixed(2) || 0}`,
    },
    {
      headerName: "Stripe Fee",
      field: "stripeFee",
      valueGetter: params => {
        const booking = params.data;
        if (booking.stripeRevenue != null) return booking.stripeRevenue;
        const grandTotal = Number(booking.grandTotal || 0);
        return ((grandTotal / 100) * 2.7 + 0.3);
      },
      flex: 1,
      filter: true,
      valueFormatter: params => `₹${params?.value?.toFixed(2) || 0}`,
      cellStyle: { color: "#dc3545" },
    },
    {
      headerName: "Eventox Revenue",
      field: "eventoxRevenue",
      valueGetter: params => params?.data?.eventoxRevenue || 0,
      flex: 1,
      filter: true,
      valueFormatter: params => `₹${params?.value?.toFixed(2) || 0}`,
      cellStyle: { fontWeight: "bold", color: "#198754" },
    },
    {
      headerName: "Organizer Revenue",
      field: "organizerRevenue",
      valueGetter: params => {
        const booking = params.data;
        const totalTicketAmount = Number(booking.totalTicketAmount || 0);
        const discount = Number(booking.discountAmount || 0);
        return (totalTicketAmount - discount);
      },
      flex: 1,
      filter: true,
      valueFormatter: params => `₹${params.value.toFixed(2)}`,
      cellStyle: { color: "#0d6efd" },
    },
    {
      headerName: "Grand Total",
      field: "grandTotal",
      valueGetter: params => params.data.grandTotal || 0,
      flex: 1,
      filter: true,
      valueFormatter: params => `₹${params.value.toFixed(2)}`,
      cellStyle: { fontWeight: "bold", color: "#6f42c1" },
    },
    {
      headerName: "Created Date",
      field: "createdAt",
      valueGetter: params => moment(params.data.createdAt).format("DD MMM YYYY"),
      flex: 1,
      filter: true,
      sortable: true,
    },
    {
      headerName: "Actions",
      field: "actions",
      flex: 1,
      sortable: false,
      filter: false,
      cellRenderer: params => {
        return (
          <Button
            color="primary"
            size="sm"
            onClick={() => navigateToBookingDetails(params.data.bookingNo)}
            className="btn-sm"
          >
            <i className="bx bx-show me-1"></i>
            View Details
          </Button>
        );
      },
    },
  ];

  // Calculate summary statistics
  const calculateSummaryStats = (bookings) => {
    if (!bookings || bookings.length === 0) {
      setSummaryStats({
        totalRevenue: 0,
        totalEventoxRevenue: 0,
        totalOrganizerRevenue: 0,
        totalStripeFee: 0,
        totalBookings: 0,
        totalTickets: 0,
        averageTicketPrice: 0
      });
      return;
    }

    const stats = bookings.reduce((acc, booking) => {
      const revenue = calcRevenueDetails(booking);
      acc.totalRevenue += revenue.grandTotal;
      acc.totalEventoxRevenue += revenue.eventoxRevenue;
      acc.totalOrganizerRevenue += revenue.organizerRevenue;
      acc.totalStripeFee += revenue.stripeRevenue;
      acc.totalBookings++;
      acc.totalTickets += Number(booking.totalTicketCount || 0);
      return acc;
    }, {
      totalRevenue: 0,
      totalEventoxRevenue: 0,
      totalOrganizerRevenue: 0,
      totalStripeFee: 0,
      totalBookings: 0,
      totalTickets: 0
    });

    stats.averageTicketPrice = stats.totalTickets > 0 ? stats.totalRevenue / stats.totalTickets : 0;
    setSummaryStats(stats);
  };

  // Fetch and calculate revenue
  const fetchRevenue = async () => {
    setLoading(true);
    setError("");
    setSummaryStats({
      totalRevenue: 0,
      totalEventoxRevenue: 0,
      totalOrganizerRevenue: 0,
      totalStripeFee: 0,
      totalBookings: 0,
      totalTickets: 0,
      averageTicketPrice: 0
    });
    setBookings([]);

    try {
      const data = await getAllBookingByDateRange(fromDate, toDate);
      // Filter confirmed bookings, bookedBy == 0 or "0"
      const confirmed = data.filter(
        booking =>
          booking.bookingStatus === "confirmed" &&
          (booking.bookedBy === "0" || booking.bookedBy === 0)
      );
      setBookings(confirmed);
      calculateSummaryStats(confirmed);
      // Save state after successful fetch
      setTimeout(() => saveSearchState(), 100);
    } catch (err) {
      setError("Failed to fetch revenue data.");
      setBookings([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Breadcrumb */}
          <Breadcrumbs
            title={props.t("Dashboards")}
            breadcrumbItem={props.t("Revenue Report")}
          />

          {/* Search Form */}
          <Card className="mb-4">
            <CardBody>
              <div className="d-flex align-items-center mb-4">
                <div className="bg-primary bg-gradient rounded-circle p-3 me-3">
                  <i className="bx bx-dollar-circle text-white fs-4"></i>
                </div>
                <div>
                  <h4 className="card-title mb-1">Revenue Report</h4>
                  <p className="text-muted mb-0">Track Eventox revenue and financial metrics</p>
                </div>
              </div>

              {/* Date filter row */}
              <Row className="align-items-end gy-3">
                <Col xs="12" md="3" lg="3">
                  <label className="form-label fw-semibold">
                    <i className="bx bx-calendar me-2"></i>
                    From Date
                  </label>
                  <FlatPickr
                    className="form-control"
                    value={fromDate}
                    onChange={(date) => setFromDate(date[0])}
                    options={{
                      dateFormat: "Y-m-d",
                      placeholder: "Select from date",
                      maxDate: toDate || undefined,
                      allowInput: false,
                      clickOpens: true,
                      disableMobile: false
                    }}
                  />
                </Col>
                <Col xs="12" md="3" lg="3">
                  <label className="form-label fw-semibold">
                    <i className="bx bx-calendar me-2"></i>
                    To Date
                  </label>
                  <FlatPickr
                    className="form-control"
                    value={toDate}
                    onChange={(date) => setToDate(date[0])}
                    options={{
                      dateFormat: "Y-m-d",
                      placeholder: "Select to date",
                      minDate: fromDate || undefined,
                      allowInput: false,
                      clickOpens: true,
                      disableMobile: false
                    }}
                  />
                </Col>
                <Col xs="12" md="3" lg="3">
                  <Button
                    color="primary"
                    onClick={fetchRevenue}
                    disabled={!fromDate || !toDate || loading}
                    className="w-100"
                    style={{ marginTop: '32px' }}
                  >
                    {loading ? (
                      <>
                        <i className="bx bx-loader-alt bx-spin me-2"></i>
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="bx bx-search me-2"></i>
                        Get Revenue
                      </>
                    )}
                  </Button>
                </Col>
                <Col xs="12" md="3" lg="3">
                  <Button
                    color="secondary"
                    onClick={() => {
                      setFromDate("");
                      setToDate("");
                      setBookings([]);
                      setError("");
                      setStateRestored(false);
                      clearSearchState();
                    }}
                    className="w-100"
                    style={{ marginTop: '32px' }}
                  >
                    <i className="bx bx-reset me-2"></i>
                    Clear
                  </Button>
                </Col>
              </Row>

              {/* State Restored Notification */}
              {stateRestored && (
                <Alert color="info" className="mt-3">
                  <i className="bx bx-refresh me-2"></i>
                  Previous search results have been restored. You can continue from where you left off.
                </Alert>
              )}

              {/* Error Message */}
              {error && (
                <Alert color="danger" className="mt-3">
                  <i className="bx bx-error-circle me-2"></i>
                  {error}
                </Alert>
              )}
            </CardBody>
          </Card>

          {/* Summary Statistics */}
          {summaryStats.totalBookings > 0 && (
            <Card className="mb-4">
              <CardBody>
                <h5 className="card-title mb-4">
                  <i className="bx bx-bar-chart-alt-2 me-2 text-primary"></i>
                  Summary Statistics
                </h5>

                {/* Main Statistics */}
                <Row className="mb-4">
                  <Col lg={3} md={6} className="mb-3">
                    <div className="border rounded p-3 text-center h-100">
                      <div className="text-primary mb-2">
                        <i className="bx bx-dollar-circle fs-2"></i>
                      </div>
                      <h4 className="text-dark mb-1 fw-bold">₹{summaryStats?.totalRevenue?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                      <p className="text-muted mb-0">Total Collected Amount</p>
                    </div>
                  </Col>
                  <Col lg={3} md={6} className="mb-3">
                    <div className="border rounded p-3 text-center h-100">
                      <div className="text-success mb-2">
                        <i className="bx bx-trending-up fs-2"></i>
                      </div>
                      <h4 className="text-dark mb-1 fw-bold">₹{summaryStats?.totalEventoxRevenue?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                      <p className="text-muted mb-0">Eventox Revenue</p>
                    </div>
                  </Col>
                    <Col lg={3} md={6} className="mb-3">
                    <div className="border rounded p-3 text-center h-100">
                      <div className="text-info mb-2">
                        <i className="bx bx-calendar-check fs-2"></i>
                      </div>
                      <h4 className="text-dark mb-1 fw-bold">₹{summaryStats?.totalOrganizerRevenue?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h4>
                      <p className="text-muted mb-0">Organizer Revenue</p>
                    </div>
                  </Col>
                  
                  <Col lg={3} md={6} className="mb-3">
                    <div className="border rounded p-3 text-center h-100">
                      <div className="text-info mb-2">
                        <i className="bx bx-calendar-check fs-2"></i>
                      </div>
                      <h4 className="text-dark mb-1 fw-bold">{summaryStats?.totalBookings?.toLocaleString()}</h4>
                      <p className="text-muted mb-0">Total Bookings</p>
                    </div>
                  </Col>
                </Row>

                {/* Additional Metrics */}
                <Row>
                  <Col lg={4} md={6} className="mb-3">
                    <div className="border rounded p-3 text-center bg-light">
                      <div className="text-warning mb-2">
                        <i className="bx bx-ticket fs-3"></i>
                      </div>
                      <h6 className="mb-1 text-dark">Total Tickets</h6>
                      <h5 className="text-warning mb-0 fw-bold">
                        {summaryStats.totalTickets.toLocaleString()}
                      </h5>
                    </div>
                  </Col>
                  <Col lg={4} md={6} className="mb-3">
                    <div className="border rounded p-3 text-center bg-light">
                      <div className="text-info mb-2">
                        <i className="bx bx-calculator fs-3"></i>
                      </div>
                      <h6 className="mb-1 text-dark">Avg. Ticket Price</h6>
                      <h5 className="text-info mb-0 fw-bold">
                        ₹{summaryStats.averageTicketPrice.toFixed(2)}
                      </h5>
                    </div>
                  </Col>
                  <Col lg={4} md={6} className="mb-3">
                    <div className="border rounded p-3 text-center bg-light">
                      <div className="text-success mb-2">
                        <i className="bx bx-percentage fs-3"></i>
                      </div>
                      <h6 className="mb-1 text-dark">Revenue Margin</h6>
                      <h5 className="text-success mb-0 fw-bold">
                        {summaryStats.totalRevenue > 0 ? ((summaryStats.totalEventoxRevenue / summaryStats.totalRevenue) * 100).toFixed(1) : 0}%
                      </h5>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {/* Data Table */}
          {bookings.length > 0 && (
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0">
                    <i className="bx bx-table me-2 text-primary"></i>
                    Revenue Details
                  </h5>
                  <Badge color="success" className="fs-6">
                    {bookings.length} Records
                  </Badge>
                </div>

                <div
                  style={{
                    height: "600px",
                    width: "100%",
                    overflow: "hidden"
                  }}
                  className="ag-theme-alpine"
                >
                  <AgGridReact
                    columnDefs={columns}
                    rowData={bookings}
                    ref={gridApiRef}
                    defaultColDef={{
                      floatingFilter: true,
                      filter: "agTextColumnFilter",
                      sortable: true,
                      resizable: true,
                      enableRowGroup: true,
                      minWidth: 120,
                      maxWidth: 300,
                    }}
                    sideBar={{ toolPanels: ["columns", "filters"] }}
                    rowGroupPanelShow="always"
                    pagination
                    paginationPageSize={paginationPageSize}
                    paginationPageSizeSelector={paginationPageSizeSelector}
                    suppressRowClickSelection={true}
                    enableRangeSelection={true}
                    suppressColumnVirtualisation={false}
                    suppressRowVirtualisation={false}
                    domLayout="normal"
                  />
                </div>
              </CardBody>
            </Card>
          )}

          {/* No Data Message */}
          {!loading && bookings.length === 0 && (fromDate || toDate) && (
            <Card>
              <CardBody className="text-center py-5">
                <i className="bx bx-data text-muted" style={{ fontSize: '4rem' }}></i>
                <h5 className="mt-3 text-muted">No Revenue Data Found</h5>
                <p className="text-muted">Try adjusting your date range or check if there are any confirmed bookings in the selected period.</p>
              </CardBody>
            </Card>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(EventoxRevenueInDateRange);
